import { useRef } from "react";
import { useTranslations } from "use-intl";
import { toggleViewportDebug } from "@/components/app/viewport-debug";
import { ACHIEVEMENTS, levelFromXp, rankForLevel } from "@liftquest/shared/gamification";
import { Page, PageHeader, Section } from "@/components/app/page-header";
import { LevelBadge, XpBar } from "@/components/app/xp-bar";
import { Card, CardContent } from "@/components/ui/card";
import { useMe } from "@/lib/queries";
import { cn } from "@/lib/utils";
import { McpSection } from "./profile-mcp";
import { AccountActions, DataExport, SettingsForm } from "./profile-settings";

export function ProfilePage() {
  const { user, profile, achievements, features } = useMe().data!;
  const t = useTranslations("profile");
  const tr = useTranslations("ranks");
  const ta = useTranslations("achievements");
  const td = useTranslations("dashboard");
  const lvl = levelFromXp(profile.xp);
  const unlocked = new Map(achievements.map((a) => [a.key, a.unlockedAt]));

  return (
    <Page>
      <PageHeader title={t("title")} />
      <Card className="mt-5 py-4">
        <CardContent className="flex items-center gap-4 px-4">
          <LevelBadge level={lvl.level} className="size-16 text-2xl" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-lg font-bold">{user.name}</p>
            <p className="truncate text-sm text-muted-foreground">{user.email}</p>
            <p className="mt-1 text-sm font-medium">
              {td("level", { level: lvl.level })} · {tr(rankForLevel(lvl.level))}
            </p>
            <XpBar progress={lvl.progress} className="mt-1.5" />
            <p className="mt-1 text-xs text-muted-foreground tabular-nums">{profile.xp} XP</p>
          </div>
        </CardContent>
      </Card>

      <SettingsForm
        initial={{
          beginnerMode: profile.beginnerMode,
          weeklyGoal: profile.weeklyGoal,
          soundEnabled: profile.soundEnabled,
          vibrationEnabled: profile.vibrationEnabled,
        }}
      />

      <Section title={t("achievements")}>
        <p id="achievements" className="-mt-1 mb-2 scroll-mt-4 text-xs text-muted-foreground">
          {t("unlocked", { count: unlocked.size, total: ACHIEVEMENTS.length })}
        </p>
        <ul className="grid grid-cols-3 gap-2">
          {ACHIEVEMENTS.map((a) => {
            const has = unlocked.has(a.key);
            return (
              <li
                key={a.key}
                className={cn(
                  "flex flex-col items-center rounded-2xl border p-2.5 text-center",
                  has ? "border-xp/40 bg-xp/10" : "opacity-45 grayscale",
                )}
              >
                <span className="text-2xl">{has ? a.icon : "🔒"}</span>
                <span className="mt-1 text-[11px] font-semibold leading-tight">
                  {ta(`${a.key}.title` as "first_workout.title")}
                </span>
                <span className="mt-0.5 text-[10px] leading-tight text-muted-foreground">
                  {ta(`${a.key}.desc` as "first_workout.desc")}
                </span>
              </li>
            );
          })}
        </ul>
      </Section>

      {features.mcp && <McpSection />}

      <DataExport />

      <AccountActions />

      <VersionFooter />
    </Page>
  );
}

/** App version; tapping it 5× quickly toggles the hidden viewport diagnostics. */
function VersionFooter() {
  const taps = useRef<number[]>([]);
  return (
    <button
      type="button"
      className="mx-auto mt-8 block select-none text-xs text-muted-foreground/70"
      onClick={() => {
        const now = Date.now();
        taps.current = [...taps.current.filter((t) => now - t < 2000), now];
        if (taps.current.length >= 5) {
          taps.current = [];
          toggleViewportDebug();
        }
      }}
    >
      LiftQuest {__APP_VERSION__}
    </button>
  );
}
